import React from 'react';

import ChatIcon from '@mui/icons-material/Chat';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import SaveIcon from '@mui/icons-material/Save';
import SettingsIcon from '@mui/icons-material/Settings';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import WorkflowArticleLayout from './components/WorkflowArticleLayout';
import WorkflowChatBubble from './components/WorkflowChatBubble';
import WorkflowCodeBlock from './components/WorkflowCodeBlock';
import { WorkflowFlowArrow, WorkflowFlowStep } from './components/WorkflowFlowStep';
import WorkflowInfoBox from './components/WorkflowInfoBox';
import WorkflowLearningPoints from './components/WorkflowLearningPoints';
import WorkflowList from './components/WorkflowList';
import WorkflowOutputBox from './components/WorkflowOutputBox';
import WorkflowPracticeBox from './components/WorkflowPracticeBox';
import WorkflowSection from './components/WorkflowSection';
import WorkflowTable from './components/WorkflowTable';
import WorkflowTableOfContents from './components/WorkflowTableOfContents';
import WorkflowText from './components/WorkflowText';

const reqSteps = [
  { label: 'Part1', title: 'RFPを作成する', path: '/devdocs/workflow/req-step1' },
  {
    label: 'Part2',
    title: 'AI対話で仕様を策定する',
    path: '/devdocs/workflow/req-step2',
    active: true,
  },
  { label: 'Part3', title: 'proposal/businessを出力する', path: '/devdocs/workflow/req-step3' },
  { label: 'Part4', title: 'システムユースケースを出力する', path: '/devdocs/workflow/req-step4' },
];

const WorkflowReqStep2: React.FC = () => {
  return (
    <WorkflowArticleLayout
      title="要件定義編 Part2 — AI対話で仕様を策定する"
      seriesName="要件定義編"
      tags={['Requirements', 'AI対話']}
      seriesSteps={reqSteps}
      prevLink={{ label: 'Part1: RFP作成', path: '/devdocs/workflow/req-step1' }}
      nextLink={{ label: 'Part3: proposal/business出力', path: '/devdocs/workflow/req-step3' }}
    >
      {/* この記事で学べること */}
      <WorkflowLearningPoints
        items={[
          'AIとの対話で仕様を策定する進め方',
          '対話パターン（選択肢・深掘り・提案修正・矛盾指摘・ファイル直接記入）',
          '決定事項をRFPに蓄積する方法と理由',
          'FileMatchによるSteering自動読み込みの仕組み',
        ]}
      />

      {/* 目次 */}
      <WorkflowTableOfContents
        items={[
          { id: 'section-1', label: '概要' },
          { id: 'section-2', label: '対話の進め方' },
          { id: 'section-3', label: '決定すべき事項チェックリスト' },
          { id: 'section-4', label: 'このパートの出力イメージ' },
          { id: 'section-5', label: '注意すべきこと' },
          { id: 'section-6', label: 'ベストプラクティス' },
          { id: 'section-7', label: 'アンチパターン' },
        ]}
      />

      {/* 概要 */}
      <WorkflowSection title="概要" id="section-1" number={1}>
        <WorkflowTable
          headers={['項目', '内容']}
          rows={[
            [
              '前提条件',
              'Part1が完了し、.rfp.md ファイルが作成されている。RFPに機能の概要・対象ユーザー・主要な操作が記載されている',
            ],
            [
              '完了条件',
              'RFPの「決定事項」セクションにテーブル設計・API設計・ビジネスルール・認証認可が記載されている。決定事項に「未定」「要検討」が残っていない',
            ],
          ]}
        />
        <WorkflowText>
          Part1で作成したRFPを元に、Kiro（AI）と対話しながら仕様を詰めていきます。
          AIが質問を投げかけ、人間が回答する。このサイクルを繰り返すことで、曖昧なアイデアが具体的な仕様に変わっていきます。
        </WorkflowText>
        <WorkflowText>
          このパートで確定した内容は最終的にPart3で proposal.md / business.md として構造化されます。
        </WorkflowText>
      </WorkflowSection>

      {/* 対話の進め方 */}
      <WorkflowSection title="対話の進め方" id="section-2" number={2}>
        {/* 1. RFPファイルを開いた状態でAIに指示する */}
        <WorkflowSection title="RFPファイルを開いた状態でAIに指示する" subNumber="2.1">
          <WorkflowText sx={{ mb: 1 }}>
            Kiroのチャットで、RFPファイルをコンテキストに含めてAIに作業開始を指示します。
          </WorkflowText>
          <WorkflowChatBubble role="human">
            #partner.rfp.md の内容に従って、パートナー管理機能の仕様を一緒に詰めていきたい。{'\n'}
            進め方に書いてある通り、あなたから質問してください。{'\n'}
            決定した内容はpartner.rfp.mdに「## 決定事項」セクションを作って蓄積していって。
          </WorkflowChatBubble>

          <WorkflowInfoBox type="important">
            <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
              なぜ決定事項をファイルに蓄積するのか
            </Typography>
            <Typography variant="body2" color="text.secondary" component="div">
              対話だけで仕様を決めてファイルに書き出さない場合、以下の問題が発生します：
              <Box component="ul" sx={{ pl: 2, mt: 0.5, mb: 0 }}>
                <li>
                  <strong>コンテキスト枠の溢れ</strong>:
                  対話が長くなるとチャット履歴の前半がコンテキストから押し出され、前半で決めた内容をAIが参照できなくなる
                </li>
                <li>
                  <strong>コンパクションによる消失</strong>:
                  Kiroはコンテキストが限界に近づくと自動で要約圧縮するため、カラム名や制約値など細かい決定が落ちることがある
                </li>
                <li>
                  <strong>Part3での品質低下</strong>: 次のパートで proposal.md / business.md
                  を生成する際、RFPをコンテキストに渡すだけで全決定事項を参照させられる。ファイルに残っていなければAIが不完全な情報で成果物を生成してしまう
                </li>
              </Box>
              「対話で決めた → すぐRFPに書き込ませる」を徹底してください。
            </Typography>
          </WorkflowInfoBox>
        </WorkflowSection>

        {/* 2. AIが質問 → 人間が回答 */}
        <WorkflowSection title="AIが質問 → 人間が回答" subNumber="2.2">
          <WorkflowText>
            対話にはいくつかのパターンがあります。状況に応じて使い分けてください。
          </WorkflowText>

          {/* パターンA */}
          <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
            パターンA: AIが選択肢付きで質問 → 人間が選ぶ
          </Typography>
          <WorkflowText sx={{ mb: 1 }}>
            AIがメリット・デメリットを提示し、人間が判断するパターンです。
          </WorkflowText>
          <Stack spacing={1.5}>
            <WorkflowChatBubble role="ai">
              パートナーの「契約終了」の扱いについて確認させてください。方法は2つ考えられます：
              {'\n\n'}1. 論理削除フラグ（is_deleted）を使う
              {'\n'}　メリット: 実装がシンプル、既存パターンと統一
              {'\n'}　デメリット: 「削除済み」と「契約終了」の意味が混在
              {'\n\n'}2. 契約終了日（contract_end_date）で判定する
              {'\n'}　メリット: 「いつ終了したか」が明確、再開時にNULLに戻すだけ
              {'\n'}　デメリット: WHERE句に日付比較が入る
              {'\n\n'}このプロジェクトではどちらが適切ですか？
            </WorkflowChatBubble>
            <WorkflowChatBubble role="human">
              2の契約終了日で判定する方針で。再開もあり得るし、削除とは意味が違う。
            </WorkflowChatBubble>
          </Stack>

          {/* パターンB */}
          <Typography variant="body2" fontWeight={600} sx={{ mt: 2, mb: 1 }}>
            パターンB: 人間が曖昧に答える → AIが深掘りする
          </Typography>
          <WorkflowText sx={{ mb: 1 }}>
            最初から明確な回答がなくてもOKです。AIが具体化を手伝います。
          </WorkflowText>
          <Stack spacing={1.5}>
            <WorkflowChatBubble role="ai">
              パートナー一覧取得時の表示件数に上限は設けますか？
            </WorkflowChatBubble>
            <WorkflowChatBubble role="human">
              うーん、多すぎると重いから上限はほしい。
            </WorkflowChatBubble>
            <WorkflowChatBubble role="ai">
              了解です。いくつか確認させてください：
              {'\n'}・上限件数は固定値ですか？それともプロパティで設定可能にしますか？
              {'\n'}・ページネーション（次のページを取得する機能）は必要ですか？
              {'\n'}・既存のユーザー一覧（API2004）では limit=50
              をプロパティ管理していますが、同じ方式を踏襲しますか？
            </WorkflowChatBubble>
            <WorkflowChatBubble role="human">
              プロパティで設定可能に。ページネーションは今はいらない。ユーザー一覧と同じ方式で。
            </WorkflowChatBubble>
          </Stack>

          {/* パターンC */}
          <Typography variant="body2" fontWeight={600} sx={{ mt: 2, mb: 1 }}>
            パターンC: AIが提案 → 人間が修正指示
          </Typography>
          <WorkflowText sx={{ mb: 1 }}>
            AIが設計を提案し、人間が部分的に修正するパターンです。
          </WorkflowText>
          <Stack spacing={1.5}>
            <WorkflowChatBubble role="ai">
              パートナーテーブルの設計案です：
              {'\n\n'}| カラム名 | 型 | 制約 | 説明 |{'\n'}| partner_id | varchar(64) | PK |
              UUID自動採番 |{'\n'}| partner_name | varchar(100) | NOT NULL | パートナー名 |{'\n'}|
              partner_name_kana | varchar(100) | NOT NULL | パートナー名カナ |{'\n'}|
              partner_type_id | varchar(20) | NOT NULL, FK | 区分 |{'\n'}| contract_end_date |
              datetime | NULL許可 | 契約終了日 |{'\n'}| created_at | datetime | NOT NULL | 登録日時
              |{'\n'}| created_by | varchar(64) | NOT NULL | 登録者 |
            </WorkflowChatBubble>
            <WorkflowChatBubble role="human">
              概ねOK。partner_nameのmax桁数は200にして。あとcontract_start_date（契約開始日）も追加して。
            </WorkflowChatBubble>
          </Stack>

          {/* パターンD */}
          <Typography variant="body2" fontWeight={600} sx={{ mt: 2, mb: 1 }}>
            パターンD: AIが矛盾を指摘する
          </Typography>
          <WorkflowText sx={{ mb: 1 }}>対話の中で矛盾が出た場合、AIが指摘します。</WorkflowText>
          <Stack spacing={1.5}>
            <WorkflowChatBubble role="ai">
              確認です。先ほど「論理削除は使わない」と決定しましたが、今おっしゃった「パートナーを無効にする機能」は実質的に論理削除と同じ動きになります。
              {'\n\n'}整合性を取るために、以下のどちらかに統一しませんか？
              {'\n'}1. 「無効化」ではなく「契約終了日を設定する」操作にする
              {'\n'}2. 「論理削除」ではなく「ステータス管理」として設計し直す
            </WorkflowChatBubble>
            <WorkflowChatBubble role="human">
              1で。契約終了日を設定する操作に統一する。
            </WorkflowChatBubble>
          </Stack>

          {/* パターンE */}
          <Typography variant="body2" fontWeight={600} sx={{ mt: 2, mb: 1 }}>
            パターンE: ファイルに直接回答を書く
          </Typography>
          <WorkflowText sx={{ mb: 1 }}>
            回答が長い場合や、表・箇条書きで構造的に伝えたい場合は、チャットのプロンプトで頑張って打つ必要はありません。RFPファイルに直接回答を書き込んで、チャットでは「書いたよ」と伝えるだけでOKです。
          </WorkflowText>
          <WorkflowText sx={{ mb: 1 }}>
            例: AIが「APIの一覧を教えてください」と質問した場合
          </WorkflowText>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
            1. RFPファイルに直接回答を書く：
          </Typography>
          <WorkflowCodeBlock
            language="markdown"
            code={`## 1. API一覧の回答

| API ID | API名 | Method | Endpoint |
|--------|-------|--------|----------|
| API3001 | パートナー登録 | POST | /v1/partner/create |
| API3002 | パートナー更新 | PUT | /v1/partner/update |
| API3004 | パートナー取得 | GET | /v1/partner/{partnerId} |
| API3005 | パートナー一覧取得 | GET | /v1/partner/list |`}
          />
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
            2. チャットで伝える：
          </Typography>
          <WorkflowChatBubble role="human">
            partner.rfp.mdの「## 1. API一覧の回答」に回答書いたよ。確認して続けて。
          </WorkflowChatBubble>
          <WorkflowInfoBox>
            AIはファイルを読み込むので、チャットに全て打ち込む必要はありません。特に表形式のデータや複数項目の一覧は、ファイルに書いた方がフォーマットが崩れず正確に伝わります。プロンプトだけに頼らず、ファイルとプロンプトを使い分けてください。
          </WorkflowInfoBox>
        </WorkflowSection>

        {/* 3. 決定事項をRFPに蓄積する */}
        <WorkflowSection title="決定事項をRFPに蓄積する" subNumber="2.3">
          <WorkflowText sx={{ mb: 1 }}>
            対話で決まった内容は、こまめにAIにRFPへ書き込ませます。
          </WorkflowText>
          <WorkflowChatBubble role="human">
            今決まった内容をpartner.rfp.mdの決定事項セクションに追記して。
          </WorkflowChatBubble>
          <WorkflowInfoBox type="warning">
            この時点ではRFPに蓄積するだけです。proposal.md / business.md
            への構造化出力はPart3で行います。
          </WorkflowInfoBox>
          <WorkflowInfoBox>
            <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
              対話回数の目安
            </Typography>
            1つの機能につき10〜30回程度の往復対話が目安です。それより多くなること自体は問題ありませんが、50回を超えても決まりきらない場合は機能の粒度が大きすぎる可能性があります。AIの制約ではなくシステム設計の観点から、機能を分割することを検討してください。「決める対象が2つ以上の独立した概念にまたがっている」と感じたら分割のサインです。
          </WorkflowInfoBox>
        </WorkflowSection>

        {/* 4. 対話を効率よく進めるプロンプト例 */}
        <WorkflowSection title="対話を効率よく進めるプロンプト例" subNumber="2.4">
          <Typography variant="body2" fontWeight={600} sx={{ mt: 1.5, mb: 0.5 }}>
            AIに既存設計書を参照させる
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
            （パターンA・Cで特に有効 — AIが既存パターンを踏まえた選択肢や提案を出してくれる）
          </Typography>
          <WorkflowChatBubble role="human">
            既存のスキーマ定義（docs/20_BD/10_スキーマ定義/）を見て、{'\n'}
            パートナー管理に使えそうな既存テーブルや参照すべき設計パターンがあれば教えて。
          </WorkflowChatBubble>

          <Typography variant="body2" fontWeight={600} sx={{ mt: 1.5, mb: 0.5 }}>
            AIに提案させて選ぶ
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
            （パターンAの典型 — メリデメ付き選択肢をAIに出させて人間が選ぶ）
          </Typography>
          <WorkflowChatBubble role="human">
            パートナーの削除方法について、論理削除と物理削除のメリデメを教えて。{'\n'}
            このプロジェクトにはどちらが適切？
          </WorkflowChatBubble>

          <Typography variant="body2" fontWeight={600} sx={{ mt: 1.5, mb: 0.5 }}>
            曖昧な部分をAIに指摘させる
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
            （パターンBの起点になる — AIが深掘り質問を返してくれる）
          </Typography>
          <WorkflowChatBubble role="human">
            ここまでの決定事項を見て、まだ決まっていないことや曖昧な部分を洗い出して。
          </WorkflowChatBubble>

          <Typography variant="body2" fontWeight={600} sx={{ mt: 1.5, mb: 0.5 }}>
            答えが分からないときはAIに聞き返す
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
            （パターンBの応用 — 判断材料がないときに既存実装を参照させる）
          </Typography>
          <WorkflowChatBubble role="human">
            今の質問に答えるための判断材料がない。{'\n'}
            このプロジェクトの既存実装（ユーザー管理）ではどうなっている？{'\n'}
            それを参考に提案して。
          </WorkflowChatBubble>
          <WorkflowInfoBox>
            「分からない」と正直に言って大丈夫です。AIは既存コードや設計書を参照して判断材料を揃えてくれます。自分で調べてから回答しようとするより、AIに調査を任せた方が対話のテンポが良くなります。
          </WorkflowInfoBox>

          <Typography variant="body2" fontWeight={600} sx={{ mt: 1.5, mb: 0.5 }}>
            数値・IDは人間が決める
          </Typography>
          <WorkflowInfoBox type="important">
            API
            IDの採番、テーブルのバージョン番号、カラムの桁数など、具体的な数値はAIに勝手に決めさせず、必ず人間が確認・決定してください。AIには「提案」させ、人間が「採用/却下」する形を取ります。
          </WorkflowInfoBox>
        </WorkflowSection>
      </WorkflowSection>

      {/* 決定すべき事項チェックリスト */}
      <WorkflowSection title="決定すべき事項チェックリスト" id="section-3" number={3}>
        {/* FileMatchの仕組みフロー図 */}
        <WorkflowSection
          title="なぜRFPにこのチェックリストを書かなくていいのか — FileMatchの仕組み"
          subNumber="3.1"
        >
          <WorkflowText>
            このプロジェクトでは、proposalのSteeringルール（create_proposal.md）に fileMatchPattern
            が設定されています。
          </WorkflowText>

          {/* 4ステップのフロー図（ファイル操作・ルール参照付き） */}
          <Box
            sx={{
              border: 1,
              borderColor: 'divider',
              borderRadius: 2,
              p: 2.5,
              mb: 2,
            }}
          >
            <Stack spacing={0}>
              <WorkflowFlowStep
                step="1"
                icon={<NoteAddIcon sx={{ fontSize: 22 }} />}
                title="RFPのゴールに「proposal.mdを作成する」と書く"
                desc="RFPファイルに作りたい成果物を明記するだけ。チェックリストや確認項目を書く必要はない。"
                files={[
                  {
                    name: 'partner.rfp.md',
                    action: '読み込み + 書き込み',
                    note: '人間が編集（Part1で作成済み）',
                  },
                ]}
                rules={[]}
                ruleNote="RFPの「進め方」セクションに対話ルールが記載されている（Part1で作成済み）"
              />
              <WorkflowFlowArrow label="AIが *.rfp.md を読み込む → fileMatch 発動" />

              <WorkflowFlowStep
                step="2"
                icon={<SettingsIcon sx={{ fontSize: 22 }} />}
                title="Steeringルールが自動で読み込まれる"
                desc="create_proposal.md の globs に *.rfp.md が含まれているため、RFPをコンテキストに入れた時点でルールが自動適用される。"
                files={[
                  {
                    name: 'partner.rfp.md',
                    action: '読み込み',
                    note: '#ファイル名 でコンテキストに投入',
                  },
                ]}
                rules={[
                  { name: 'create_proposal.md', trigger: 'fileMatch: **/*.rfp.md' },
                  { name: 'create_business.md', trigger: 'fileMatch: **/*.rfp.md' },
                ]}
                ruleNote="これらのSteeringに「何を確認すべきか」のチェックリストが定義されている"
              />
              <WorkflowFlowArrow label="Steering内の確認事項リストに従って対話開始" />

              <WorkflowFlowStep
                step="3"
                icon={<ChatIcon sx={{ fontSize: 22 }} />}
                title="AIが対話でテーブル設計・API設計等を自動で聞いてくる"
                desc="Steeringに「テーブル定義を決めろ」「API一覧を確認しろ」と書いてあるので、AIが自発的に質問する。"
                files={[
                  { name: 'partner.rfp.md', action: '読み込み', note: '決定済み内容を随時参照' },
                ]}
                rules={[
                  {
                    name: 'create_proposal.md',
                    trigger: 'テーブル設計・API設計・認証認可の確認項目',
                  },
                  {
                    name: 'create_business.md',
                    trigger: 'ユースケース・画面フロー・入力項目の確認項目',
                  },
                  {
                    name: 'partner.rfp.md（進め方セクション）',
                    trigger: '対話の進め方・質問の順序・回答形式のルール',
                  },
                ]}
                ruleNote="Steeringの確認項目 + RFP内の対話ルールの両方に従い、AIが質問を組み立てる"
              />
              <WorkflowFlowArrow label="決定事項が出たらRFPに書き込み" />

              <WorkflowFlowStep
                step="4"
                icon={<SaveIcon sx={{ fontSize: 22 }} />}
                title="合意した内容がRFPの決定事項セクションに蓄積される"
                desc="対話で決まったことをAIがRFPに書き込む。Step3でこの内容が proposal.md / business.md に構造化される。"
                files={[
                  {
                    name: 'partner.rfp.md',
                    action: '書き込み',
                    note: '## 決定事項 セクションに追記',
                  },
                ]}
                rules={[
                  {
                    name: 'create_proposal.md',
                    trigger: '書き込み時にも fileMatch で参照され続ける',
                  },
                ]}
                ruleNote="RFPに書き込むたびにSteeringが参照され、決定事項のフォーマットが制御される"
              />
            </Stack>
          </Box>

          <WorkflowTable
            headers={['メリット', '説明']}
            rows={[
              ['一元管理', 'proposalのSteeringルールを修正すれば、全てのRFP対話に一律で反映される'],
              [
                '品質の均一化',
                'RFPごとに決定事項のばらつきが出ない。どの機能でも同じ観点で仕様が確認される',
              ],
            ]}
          />
          <WorkflowText sx={{ mt: 1 }}>
            RFPに決定事項の一覧を書いてしまうと、Steeringルールと二重管理になります。RFPには「対話の進め方」だけを書き、何を決めるかはSteeringに任せてください。
          </WorkflowText>
          <WorkflowInfoBox>
            これがSteeringの fileMatch
            の典型的な使い方です。「特定のファイルパターンに対してルールを自動適用する」ことで、個別のファイルにルールを書く必要がなくなります。このパターンを覚えておくと、自分のプロジェクトでもSteering設計に活かせます。
          </WorkflowInfoBox>
        </WorkflowSection>

        {/* チェックリスト */}
        <WorkflowSection title="チェックリスト（本プロジェクトの例）" subNumber="3.2">
          <WorkflowText sx={{ mb: 1 }}>
            以下は本プロジェクト（Java / Spring Boot / REST
            API）のSteeringルールで定義されている確認項目です。対話の進捗確認に使ってください。
          </WorkflowText>

          <Typography variant="body2" fontWeight={600} sx={{ mt: 1.5, mb: 0.5 }}>
            proposal（技術仕様）で決める事項
          </Typography>
          <WorkflowTable
            headers={['カテゴリ', '決定事項', '例']}
            rows={[
              ['テーブル設計', '必要なテーブル一覧', 'partner, master_type'],
              ['テーブル設計', '各テーブルのカラム・型・桁数・制約', 'partner_id: varchar(64), PK'],
              ['テーブル設計', 'インデックス・ユニークキー', 'partner_name_kana_idx'],
              ['テーブル設計', '初期データ（マスタ区分）', 'partner_type: CORPORATE, INDIVIDUAL'],
              [
                'API設計',
                'API一覧（ID, 名前, Method, Endpoint）',
                'API3001, POST /v1/partner/create',
              ],
              [
                'API設計',
                '各APIのRequest/Response・エラーケース',
                '登録→パートナーIDを返却、重複時422',
              ],
              ['認証・認可', '各APIに必要な権限', 'ADMIN, BACK_OFFICE, MANAGER'],
              [
                'ビジネスルール',
                '削除方式・採番・ステータス遷移',
                '契約終了判定: contract_end_date > now',
              ],
              ['プロパティ', '設定可能な値（limit等）', 'partner.limit-size = 50'],
            ]}
          />

          <Typography variant="body2" fontWeight={600} sx={{ mt: 1.5, mb: 0.5 }}>
            business（ユーザー操作）で決める事項
          </Typography>
          <WorkflowTable
            headers={['カテゴリ', '決定事項', '例']}
            rows={[
              ['ユースケース', 'ユーザーが達成したい目的の全量', '登録/更新/詳細確認/検索'],
              ['操作者', '各操作を誰が行うか（権限含む）', '管理者（ADMIN/BACK_OFFICE/MANAGER）'],
              [
                '画面フロー',
                'どの画面からどの順序で操作するか',
                '一覧画面 → 詳細画面 → 編集ダイアログ',
              ],
              ['入力項目', '必須/任意・形式・桁数', 'パートナー名: 必須, テキスト, 200文字'],
              ['注意事項', '間違えやすいポイント・不可逆操作', '削除は復元不可'],
            ]}
          />

          <WorkflowInfoBox type="warning">
            <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
              注意: このチェックリストはプロジェクトによって変わります
            </Typography>
            <Typography variant="body2" color="text.secondary" component="div">
              上記は本プロジェクト（Java / REST API /
              MySQL）で必要な項目です。実際に決めるべき事項は、使う言語・技術スタック・システムの種類によって異なります：
              <Box component="ul" sx={{ pl: 2, mt: 0.5, mb: 0 }}>
                <li>フロントエンド: コンポーネント設計、状態管理、ルーティング等が追加される</li>
                <li>バッチ処理: 実行タイミング、リトライ戦略、エラー通知が追加される</li>
                <li>マイクロサービス: サービス間通信、イベント設計、整合性保証が追加される</li>
                <li>
                  インフラ・環境: デプロイ先（AWS / GCP /
                  オンプレ）、CI/CDパイプライン、環境変数管理が追加される
                </li>
              </Box>
              自分のプロジェクトのSteeringルールを作る際は、これを参考に項目を追加・削除してください。
            </Typography>
          </WorkflowInfoBox>
        </WorkflowSection>
      </WorkflowSection>

      {/* このパートの出力イメージ */}
      <WorkflowSection title="このパートの出力イメージ" id="section-4" number={4}>
        <WorkflowText sx={{ mb: 1 }}>
          対話が完了すると、RFPの「決定事項」セクションに以下のような内容が蓄積されます。この内容がStep3で
          proposal.md / business.md に構造化されます：
        </WorkflowText>
        <WorkflowCodeBlock
          language="markdown"
          code={`## 決定事項

### 概要
- Partner = メンバーが所属する企業（法人 or 個人事業主）
- 関係: Partner 1 : N Member

### 認証・認可
- 全パートナーAPI共通: ADMIN, BACK_OFFICE, MANAGER

### 契約終了の扱い
- 論理削除フラグは使用しない
- contract_end_date > 現在日時 の場合を「契約終了」とする

### テーブル定義
（カラム定義の詳細...）

### API一覧
| API ID | API名 | Method | Endpoint |
|--------|-------|--------|----------|
| API3001 | パートナー登録API | POST | /v1/partner/create |
| ...    | ...   | ...    | ...      |

### API概要
（各APIの入出力概要...）`}
        />
      </WorkflowSection>

      {/* 注意すべきこと */}
      <WorkflowSection title="注意すべきこと" id="section-5" number={5}>
        <WorkflowTable
          headers={['注意点', '説明']}
          rows={[
            ['対話を急がない', '1回で全て決めようとせず、何度かに分けてもOK'],
            ['矛盾を見逃さない', 'AIの提案が既存設計と矛盾していないか常にチェック'],
            [
              '決定事項は必ず記録',
              '「チャットで決めた」だけでは消失する。必ずRFPの決定事項セクションに反映',
            ],
            [
              '既存パターンを活用',
              '既にある機能（ユーザー管理等）と同じパターンを踏襲させると品質が安定',
            ],
          ]}
        />
      </WorkflowSection>

      {/* ベストプラクティス */}
      <WorkflowSection title="ベストプラクティス" id="section-6" number={6}>
        <WorkflowPracticeBox
          type="best"
          items={[
            {
              practice: '1回の対話で1トピック（テーブル設計、API設計等）に絞る',
              effect: '議論が発散せず、決定事項が明確になる',
            },
            {
              practice: 'AIに既存設計書を参照させてから質問させる',
              effect: '既存パターンと整合した提案が得られる',
            },
            {
              practice: 'こまめにファイルへ書き込ませる',
              effect: '対話が長引いても途中成果が残り、セッション切替後も引き継げる',
            },
            {
              practice: '不明点はAIに「選択肢を出して」と聞く',
              effect: '自分で0から考えずに選ぶだけで進められる',
            },
            {
              practice: '対話を複数セッションに分けてもOK',
              effect:
                '長時間の対話はコンテキスト枠を圧迫する。ファイルに残っていれば次回そこから再開できる',
            },
            {
              practice: '答えが分からないときは「分からない」と正直に言う',
              effect:
                'AIが既存実装を調査して判断材料を揃えてくれる。自分で調べるより対話のテンポが良い',
            },
            {
              practice: '回答が長い・表形式の場合はファイルに直接書く',
              effect:
                'プロンプトで頑張るよりフォーマットが正確に伝わる。AIは「書いたよ」と言えば読みに行く',
            },
          ]}
        />
      </WorkflowSection>

      {/* アンチパターン */}
      <WorkflowSection title="アンチパターン" id="section-7" number={7}>
        <WorkflowPracticeBox
          type="anti"
          items={[
            {
              practice: '1回の対話で全てを決めようとする',
              effect: 'コンテキスト枠を超え、前半の決定が参照できなくなる',
            },
            {
              practice: 'AIの提案を全て無条件で受け入れる',
              effect: '既存設計との矛盾やプロジェクト固有の制約を見落とす',
            },
            {
              practice: '対話だけ続けてファイルに一切書き出さない',
              effect: 'セッション終了やコンテキストリセットで全て消失する',
            },
            {
              practice: '具体的な数値（API ID、桁数等）をAIに決めさせる',
              effect: 'プロジェクト全体の採番ルールと衝突する',
            },
            {
              practice: '「Part3でまとめて書くから今は記録しなくていい」と思う',
              effect: 'Part3時にAIが対話内容を参照できず、情報が欠落する',
            },
            {
              practice: 'Steeringが制御する決定項目をRFPに手書きする',
              effect: 'Steeringルールと二重管理になり、ルール更新時に不整合が発生する',
            },
            {
              practice: 'AIが矛盾を指摘しているのに無視して先に進む',
              effect: '後工程で設計の不整合が発覚し、手戻りが大きくなる',
            },
          ]}
        />
      </WorkflowSection>

      {/* 成果物 */}
      <WorkflowOutputBox>
        <WorkflowTable
          headers={['成果物', '説明', '利用先']}
          rows={[
            [
              '*.rfp.md（決定事項セクション更新済み）',
              '対話で確定した仕様の記録',
              'Part3で proposal.md / business.md に構造化出力',
            ],
          ]}
        />
      </WorkflowOutputBox>
    </WorkflowArticleLayout>
  );
};

export default WorkflowReqStep2;
